import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  VEHICLE_STATUSES,
  VEHICLE_TYPES,
  useSaveVehicle,
  type SaveVehicleRequest,
  type Vehicle,
} from '@/routes/fleet/useFleet';

/**
 * Add a vehicle, or edit one (M1-27).
 *
 * <b>Only the label is required</b>, matching the API and M1-25's governing
 * rule that a form asks for as little as possible before it will accept
 * anything: a shop adding "Van 3" at 7am has not looked the VIN up, and a form
 * that refuses them until they do is how a fleet list stays empty.
 *
 * ⚠️ Every `<select>` gets an explicit `htmlFor`/`id` pair rather than a
 * wrapping `<label>` — the rule M1-38's section states, and the reason is that
 * a wrapped select is announced inconsistently and cannot be reached by
 * `getByLabelText` in tests.
 */
export function VehicleFormDialog({
  vehicle,
  onClose,
  onAnnounce,
}: {
  /** Undefined when adding. */
  vehicle?: Vehicle;
  onClose: () => void;
  /**
   * Told what happened, as an already-translated sentence, so the screen can
   * put it in its live region.
   *
   * ⚠️ Required, not optional. The first version of this prop was optional and
   * nothing ever passed it, so every fleet mutation completed in silence for a
   * screen-reader user — and the axe scans passed throughout, because axe
   * cannot check that something was ANNOUNCED. Making it required is what stops
   * that reopening.
   *
   * This component does the `t()` call and hands over a finished string —
   * web/CONVENTIONS.md's M1-30 note.
   */
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const fieldId = useId();
  const save = useSaveVehicle(vehicle?.id);
  const [error, setError] = useState('');

  const [form, setForm] = useState<SaveVehicleRequest>({
    label: vehicle?.label ?? '',
    type: (vehicle?.type as SaveVehicleRequest['type']) ?? 'Van',
    make: vehicle?.make ?? null,
    model: vehicle?.model ?? null,
    // ⚠️ `int32` widens to `number | string` in the generated client — the
    // fifth field in this codebase to need the coercion (latitude, longitude,
    // sortOrder, the licence counts, and now this).
    year: vehicle?.year == null ? null : Number(vehicle.year),
    plate: vehicle?.plate ?? null,
    vin: vehicle?.vin ?? null,
    status: (vehicle?.status as SaveVehicleRequest['status']) ?? 'Active',
    notes: vehicle?.notes ?? null,
  });

  function set<K extends keyof SaveVehicleRequest>(key: K, value: SaveVehicleRequest[K]) {
    setForm((previous) => ({ ...previous, [key]: value }));
  }

  function submit() {
    const label = form.label.trim();
    if (!label) {
      setError(t('fleet.form.labelRequired'));
      return;
    }
    setError('');
    save.mutate(
      { ...form, label },
      {
        onSuccess: (saved) => {
          onAnnounce(
            vehicle
              ? t('fleet.form.saved', { label: saved.label })
              : t('fleet.form.created', { label: saved.label }),
          );
          onClose();
        },
        onError: () => setError(t('fleet.form.saveFailed')),
      },
    );
  }

  return (
    <Dialog
      titleId={titleId}
      title={vehicle ? t('fleet.form.editTitle') : t('fleet.form.addTitle')}
      onClose={onClose}
      testId="vehicle-form-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label htmlFor={`${fieldId}-label`} className="text-sm font-medium">
            {t('fleet.form.label')}
          </label>
          <input
            id={`${fieldId}-label`}
            className={FIELD_CLASS}
            value={form.label}
            onChange={(event) => set('label', event.target.value)}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor={`${fieldId}-type`} className="text-sm font-medium">
              {t('fleet.form.type')}
            </label>
            <select
              id={`${fieldId}-type`}
              className={FIELD_CLASS}
              value={form.type}
              onChange={(event) => set('type', event.target.value as SaveVehicleRequest['type'])}
            >
              {VEHICLE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`fleet.type.${type}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor={`${fieldId}-status`} className="text-sm font-medium">
              {t('fleet.form.status')}
            </label>
            <select
              id={`${fieldId}-status`}
              className={FIELD_CLASS}
              value={form.status}
              onChange={(event) =>
                set('status', event.target.value as SaveVehicleRequest['status'])
              }
            >
              {VEHICLE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`fleet.status.${status}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor={`${fieldId}-make`} className="text-sm font-medium">
              {t('fleet.form.make')}
            </label>
            <input
              id={`${fieldId}-make`}
              className={FIELD_CLASS}
              value={form.make ?? ''}
              onChange={(event) => set('make', event.target.value || null)}
            />
          </div>

          <div>
            <label htmlFor={`${fieldId}-model`} className="text-sm font-medium">
              {t('fleet.form.model')}
            </label>
            <input
              id={`${fieldId}-model`}
              className={FIELD_CLASS}
              value={form.model ?? ''}
              onChange={(event) => set('model', event.target.value || null)}
            />
          </div>

          <div>
            <label htmlFor={`${fieldId}-year`} className="text-sm font-medium">
              {t('fleet.form.year')}
            </label>
            <input
              id={`${fieldId}-year`}
              type="number"
              className={FIELD_CLASS}
              value={form.year ?? ''}
              onChange={(event) =>
                set('year', event.target.value === '' ? null : Number(event.target.value))
              }
            />
          </div>

          <div>
            <label htmlFor={`${fieldId}-plate`} className="text-sm font-medium">
              {t('fleet.form.plate')}
            </label>
            <input
              id={`${fieldId}-plate`}
              className={FIELD_CLASS}
              value={form.plate ?? ''}
              onChange={(event) => set('plate', event.target.value || null)}
            />
            {/* Said out loud because it changes what somebody types: the plate
                is destined for customer-facing messages ("look for the white
                van, plate ABC-123"), so it should read the way it does on the
                truck. M1-27 ships the FIELD only; no message reads it yet. */}
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              {t('fleet.form.plateHint')}
            </p>
          </div>
        </div>

        <div>
          <label htmlFor={`${fieldId}-vin`} className="text-sm font-medium">
            {t('fleet.form.vin')}
          </label>
          <input
            id={`${fieldId}-vin`}
            className={FIELD_CLASS}
            value={form.vin ?? ''}
            onChange={(event) => set('vin', event.target.value || null)}
          />
        </div>

        <div>
          <label htmlFor={`${fieldId}-notes`} className="text-sm font-medium">
            {t('fleet.form.notes')}
          </label>
          <textarea
            id={`${fieldId}-notes`}
            className={FIELD_CLASS}
            rows={3}
            value={form.notes ?? ''}
            onChange={(event) => set('notes', event.target.value || null)}
          />
        </div>

        {error ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
